import * as queryString from 'query-string';
import { generatePath } from 'react-router';
import { FALLBACK_REDIRECT_URL } from '../../config';
import { AccountsAppPath } from '../../types/app.types';
import { PaymentSection } from '../../types/payment.types';
import { parseAppQueryParams } from '../url';
import { PaymentError } from '../../pages/payment/payment-error.types';
import { ProductAbbreviation } from '../../types/product';

const PAYMENT_ATTEMPT_QUERY_PARAMS = [
  'payment_intent',
  'payment_intent_client_secret',
  'redirect_status',
  'error_code',
];

const omitRedirectParams = (queryParams: queryString.ParsedQuery) => {
  const cleaned = { ...queryParams };
  PAYMENT_ATTEMPT_QUERY_PARAMS.forEach((param) => delete cleaned[param]);
  return cleaned;
};

const omitRedirectParamsFromSearch = (search: string) => {
  const cleaned = queryString.stringify(omitRedirectParams(queryString.parse(search, { decode: true })), {
    encode: true,
  });
  return cleaned ? `?${cleaned}` : '';
};

export const genPaymentPlanSelectionPath = (search: string) => {
  return `${generatePath(AccountsAppPath.PAYMENT, { section: PaymentSection.PLAN_SELECTION })}${omitRedirectParamsFromSearch(search)}`;
};

export const genPaymentBillingPath = (planId: string, search: string, shouldShowSkinModal = false) => {
  const cleanedSearch = omitRedirectParamsFromSearch(search);
  const searchWithModalParam = shouldShowSkinModal ? addSkinModalShowParam(cleanedSearch) : cleanedSearch;

  return `${generatePath(AccountsAppPath.PAYMENT, {
    planId,
    section: PaymentSection.PAYMENT_INFO,
  })}${searchWithModalParam}`;
};

export const genPaymentThankYouPath = (planId: string, search: string) => {
  return `${generatePath(AccountsAppPath.PAYMENT, { planId, section: PaymentSection.THANK_YOU })}${search}`;
};

export const genAbsolutePaymentThankYouPath = (planId: string, search: string) => {
  return `${window.location.origin}${generatePath(AccountsAppPath.PAYMENT, {
    planId,
    section: PaymentSection.THANK_YOU,
  })}${omitRedirectParamsFromSearch(search)}`;
};

export const genAbsolutePaymentProcessingPath = (planId: string, search: string) => {
  return `${window.location.origin}${generatePath(AccountsAppPath.PAYMENT, {
    planId,
    section: PaymentSection.PAYMENT_PROCESSING,
  })}${omitRedirectParamsFromSearch(search)}`;
};

export const genPaymentErrorPath = (search: string, errorCode: PaymentError) => {
  const queryParams = queryString.parse(search, { decode: true });
  return `${generatePath(AccountsAppPath.PAYMENT, {
    section: PaymentSection.ERROR,
  })}?${queryString.stringify(
    {
      ...omitRedirectParams(queryParams),
      error_code: errorCode,
    },
    {
      encode: true,
    }
  )}`;
};

const addSkinModalShowParam = (search: string) => {
  const queryParams = queryString.parse(search);
  const { redirect_uri: redirectUri } = queryParams;

  if (typeof redirectUri !== 'string') {
    return search;
  }

  const isDesktopRedirect = redirectUri.includes('mobalytics-ow');
  // we need to encode for desktop here since the "returnPath" of the desktop redirect url needs to be double encoded
  const showModalParam = isDesktopRedirect
    ? encodeURIComponent('&shouldShowSkinModal=true')
    : '&shouldShowSkinModal=true';

  const searchWithModalParam = queryString.stringify({
    ...queryParams,
    redirect_uri: `${redirectUri}${showModalParam}`,
  });

  return `?${searchWithModalParam}`;
};

/**
 * Returns the build tracker landing page of the game, or null for a game without one.
 * It needs for Poe 2 release promotion. Experimental feature
 * Feel free to remove it after the promotion ends.
 */
export const getBuildTrackerPromoUrl = (productAbbreviation: string): string | null => {
  switch (productAbbreviation) {
    case ProductAbbreviation.POE_2:
      return 'https://mobalytics.gg/lol/glp/poe2-build-tracker';
    case ProductAbbreviation.DIABLO_4:
      return 'https://mobalytics.gg/lol/glp/diablo-4-build-tracker';
    default:
      return null;
  }
};

export const genPaymentV2Path = (
  search: string,
  productId: string,
  priceId: string | undefined,
  state: 'success' | 'error' | undefined,
  errorCode: PaymentError | undefined
) => {
  const queryParams = queryString.parse(search, { decode: true });
  const queryParamsWithErrorCode = {
    ...omitRedirectParams(queryParams),
    error_code: errorCode,
  };
  return `${generatePath(AccountsAppPath.PAYMENT_V2, { productId, priceId, state })}?${queryString.stringify(queryParamsWithErrorCode, { encode: true })}`;
};

export const genPaymentV2ErrorPath = (productId: string, priceId: string, search: string, errorCode: PaymentError) => {
  const queryParams = queryString.parse(search, { decode: true });
  const queryParamsWithErrorCode = {
    ...omitRedirectParams(queryParams),
    error_code: errorCode,
  };
  return `${generatePath(AccountsAppPath.PAYMENT_V2, {
    state: 'error' as const,
    productId,
    priceId,
  })}?${queryString.stringify(queryParamsWithErrorCode, { encode: true })}`;
};

export const genSignInPath = (search: string) => {
  return `${generatePath(AccountsAppPath.SIGN_IN)}${search}`;
};

export const genSignUpPath = (search: string) => {
  return `${generatePath(AccountsAppPath.SIGN_UP)}${search}`;
};

export const genRequestPasswordResetPath = (search: string) => {
  return `${generatePath(AccountsAppPath.REQUEST_PASSWORD_RESET)}${search}`;
};

export const genSupportUrl = () => {
  return `https://support.mobalytics.gg`;
};

export const genRedirectUrl = (): string => {
  const { redirect_uri, theme } = parseAppQueryParams();
  if (redirect_uri) {
    return redirect_uri;
  }
  return `${FALLBACK_REDIRECT_URL}/${theme}`;
};

export const genErrorRedirectUrl = (): string => {
  const { error_redirect_uri } = parseAppQueryParams();
  if (error_redirect_uri) {
    return error_redirect_uri;
  }

  return genRedirectUrl();
};
